/**
 * Navigation configuration for the app sidebar.
 *
 * Tab keys may use a `base:role` format (e.g. `offers:buyer`, `contingencies:seller`)
 * for components that are dual-mode and rendered from both the Buying and Selling
 * groups. Parse with {@link parseTab}.
 *
 * Items marked `requiresBilling` are hidden until GET /api/billing/config
 * reports billing enabled (see App.js).
 */

export const NAV_GROUPS = [
  {
    workspace: 'buyer',
    label: 'Buying',
    items: [
      { key: 'buyer-hub', icon: 'buyerHub', label: 'Buyer Hub' },
      { key: 'houses', icon: 'houses', label: 'Browse Properties' },
      { key: 'search', icon: 'search', label: 'Advanced Search' },
      { key: 'saved', icon: 'saved', label: 'Saved Searches' },
      { key: 'favorites', icon: 'favorites', label: 'Favorites' },
      { key: 'tours', icon: 'tours', label: 'Tour Requests' },
      { key: 'offers:buyer', icon: 'offers', label: 'My Offers' },
      { key: 'contingencies:buyer', icon: 'contingencies', label: 'Contingencies' },
      { key: 'affordability', icon: 'calculator', label: 'Affordability' },
      { key: 'profile', icon: 'profile', label: 'Buyer Readiness' },
      { key: 'journey:buyer', icon: 'journey', label: 'Transaction Journey' },
      { key: 'documents', icon: 'documents', label: 'Documents', secondary: true },
      { key: 'closing', icon: 'closing', label: 'Closing', secondary: true },
      { key: 'billing', icon: 'billing', label: 'Billing', secondary: true, requiresBilling: true },
      { key: 'notifications', icon: 'notifications', label: 'Notifications', secondary: true },
    ],
  },
  {
    workspace: 'seller',
    label: 'Selling',
    items: [
      { key: 'seller-hub', icon: 'sellerHub', label: 'Seller Hub' },
      { key: 'seller-listings', icon: 'listings', label: 'My Listings' },
      { key: 'seller-post', icon: 'addListing', label: 'Post a Listing' },
      { key: 'seller-tours', icon: 'listingTours', label: 'Listing Tours' },
      { key: 'offers:seller', icon: 'offers', label: 'Offers Received' },
      { key: 'contingencies:seller', icon: 'contingencies', label: 'Contingencies' },
      { key: 'journey:seller', icon: 'journey', label: 'Transaction Journey' },
      { key: 'pricing', icon: 'pricing', label: 'Plans & Pricing', secondary: true },
      { key: 'documents', icon: 'documents', label: 'Documents', secondary: true },
      { key: 'closing', icon: 'closing', label: 'Closing', secondary: true },
      { key: 'billing', icon: 'billing', label: 'Billing', secondary: true, requiresBilling: true },
      { key: 'notifications', icon: 'notifications', label: 'Notifications', secondary: true },
    ],
  },
];

/**
 * Parse a tab key of form `base:role` into its parts.
 * @param {string} tab
 * @returns {{ base: string, role: 'buyer'|'seller'|'shared'|null }}
 */
export function parseTab(tab) {
  if (typeof tab !== 'string') return { base: '', role: null };
  const idx = tab.indexOf(':');
  if (idx === -1) return { base: tab, role: null };
  const role = tab.slice(idx + 1);
  return { base: tab.slice(0, idx), role: role || null };
}

/**
 * The workspace an account starts in. Dual-role accounts start in Buying (by
 * design: their Selling side is one switch away); a seller-only account starts
 * in Selling. Shared by the landing tab and the Buy/Sell switcher so the
 * sidebar and the page agree (QA F1: seller-only accounts got the Selling
 * sidebar around the Buyer Hub).
 */
export function defaultWorkspaceForRole(role, roles = []) {
  const all = new Set(
    [role, ...(Array.isArray(roles) ? roles : [])]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase())
  );
  return all.has('seller') && !all.has('buyer') ? 'seller' : 'buyer';
}

/** Default landing tab for a given account role. */
export function defaultTabForRole(role, roles = []) {
  return defaultWorkspaceForRole(role, roles) === 'seller' ? 'seller-hub' : 'buyer-hub';
}
